<template>
  <v-container fluid class="pa-2 form-processing-material">
    <ValidationObserver class="form-processing-material-validation" v-slot="{ handleSubmit }" ref="observer">
      <form @submit.prevent="handleSubmit(startProcessing)" novalidate>
        <!-- ■加工担当者 (employee_cd) -->
        <ValidationProvider rules="required" name="processEmployee" v-slot="{ errors }">
          <v-row no-gutters class="mb-1">
            <v-col cols="12" md="5" class="text-left">
              <h4 class="header-label">■加工担当者</h4>
              <v-select class="custom-select" v-model="processEmployee" :options="processEmployeeList"
                :filter="customEmployeeFilter" option-text="社員名称" option-value="社員コード" label="社員名称"
                placeholder="担当者を選択して下さい。" />
              <span class="error-text">{{ errors[0] }}</span>
            </v-col>
          </v-row>
        </ValidationProvider>
        <!-- ■原料を指定して下さい。(purchased_item_cd) -->
        <ValidationProvider rules="required" name="purchasedItem" v-slot="{ errors }">
          <v-row no-gutters class="my-2">
            <v-col cols="12" md="5" class="text-left">
              <h4 class="header-label">■原料</h4>
              <v-select class="custom-select" ref="purchasedItem" key="purchasedItem" v-model="purchasedItem"
                :filter="customPurchasedItemFilter" :options="materialList" option-text="仕入品名称" option-value="仕入品コード"
                label="仕入品名称" placeholder="原料を選択して下さい。" @input="handlePurchasedItemChange" :disabled="isProcessing" />
              <span class="error-text">{{ errors[0] }}</span>
            </v-col>
          </v-row>
        </ValidationProvider>
        <!-- ■ブランド (bland_cd) -->
        <ValidationProvider name="brand" v-slot="{ errors }">
          <v-row no-gutters class="my-2">
            <v-col cols="12" md="5" class="text-left">
              <h4 class="header-label">■ブランド</h4>
              <v-select class="custom-select" v-model="brand" :options="brandList" :filter="customBrandFilter"
                option-text="ブランド名称" option-value="ブランドコード" label="ブランド名称" placeholder="ブランドを選択して下さい。"
                :disabled="processStatus !== PROCESS_STATUS_NOT_START" />
              <span class="error-text">{{ errors[0] }}</span>
            </v-col>
          </v-row>
        </ValidationProvider>
        <!-- ■投入量（kg） -->
        <ValidationProvider rules="required|double|positive" name="inputAmount" v-slot="{ errors }">
          <v-row no-gutters class="my-2 mb-md-6">
            <v-col cols="12" md="5" class="text-left">
              <h4 class="header-label">■投入量（kg）</h4>
              <input type="number" v-model="inputAmount" class="input-amount" placeholder="0" min="0"
                :disabled="processStatus !== PROCESS_STATUS_NOT_START" />
              <span class="error-text">{{ errors[0] }}</span>
            </v-col>
          </v-row>
        </ValidationProvider>

        <v-card class="counter-bar pa-2 flex-grow-1 flex-shrink-1" max-width="100%" outlined>
          <h4 class="text-left">■加工時間</h4>

          <v-row no-gutters>
            <v-col cols="12" md="5" class="text-md-left d-flex justify-center align-center">
              <v-card-text>
                <div>
                  <h2 class="working-time-text">{{ formatTime(totalWorkingTimeInSeconds) }}</h2>
                  <div class="pause-time-text">
                    中断時間: <span class="pause-item-value">{{ formatTime(totalPauseTimeInSeconds) }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-col>
            <v-col cols="12" md="7" class="d-flex justify-md-start justify-center align-center">
              <v-card-actions :class="processStatus === PROCESS_STATUS_NOT_START ? 'justify-center' : 'justify-center'">
                <div v-if="processStatus === PROCESS_STATUS_NOT_START">
                  <v-btn type="submit" class="font-weight-bold" color="success" :large="$vuetify.breakpoint.mdAndUp"
                    v-if="processStatus === PROCESS_STATUS_NOT_START">
                    加工開始
                  </v-btn>
                </div>
                <div v-else>
                  <v-row>
                    <v-col class="text-right pe-5">
                      <v-btn class="font-weight-bold" @click="handlePauseProcess" color="success"
                        :large="$vuetify.breakpoint.mdAndUp" v-if="processStatus === PROCESS_STATUS_WORKING">
                        加工中断
                      </v-btn>
                      <v-btn class="font-weight-bold" @click="handleWorkingProcess" color="success"
                        :large="$vuetify.breakpoint.mdAndUp" v-if="processStatus === PROCESS_STATUS_PAUSE">
                        加工再開
                      </v-btn>
                    </v-col>
                    <v-col class="text-left ps-5">
                      <v-btn class="font-weight-bold" @click="handleDoneProcess" color="success"
                        :large="$vuetify.breakpoint.mdAndUp"
                        v-if="processStatus === PROCESS_STATUS_WORKING || processStatus === PROCESS_STATUS_PAUSE">
                        加工完了
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-card-actions>
            </v-col>
          </v-row>
          <div class="error-text mt-2">※画面を閉じる場合は加工中断ボタンを<br class="d-md-none" />
            押してから画面を閉じて下さい。</div>
        </v-card>
      </form>
    </ValidationObserver>

    <div v-if="showSummaryDialog">
      <ProcessingMaterialSummaryModal :show="showSummaryDialog" :totalWorkingTimeInSeconds="totalWorkingTimeInSeconds"
        :totalPauseTimeInSeconds="totalPauseTimeComplete" :processProductItemList="processProductItemList"
        :processEmployee="processEmployee" :purchasedItem="purchasedItem" :brand="brand"
        :inputAmount="Number(inputAmount)" :onClose="handleCloseSummaryModal" :onSaveSuccess="handleEndProcess"
        :onShowError="handleErrorSummary" />
    </div>
    <AlertModal v-model="showWarningPauseProcessDialog" message="画面を閉じる場合は加工中断ボタンを押してから画面を閉じて下さい。" />
    <AlertModal v-model="showSaveProcessSuccessDialog" message="加工実績の登録が成功しました。" />
    <AlertModal v-model="showErrorSummary" title='警告' :message="showErrorMessage" />
  </v-container>
</template>

<script>
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { required, double } from 'vee-validate/dist/rules';
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import axios from "axios";
import moment from "moment";
import ProcessingMaterialSummaryModal from './ProcessingMaterialSummaryModal.vue';
import AlertModal from './AlertModal.vue';
import { normalizeToKatakana } from '../utils';

extend('required', {
  ...required,
  message: 'この項目は必須です'
});

extend('double', {
  ...double,
  message: '０以上の数値のみ入力可'
});

extend('positive', value => {
  if (value > 0) {
    return true;
  }
  return '０以上の数値のみ入力可';
});

export default {
  name: "ProcessingMaterial",
  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    ProcessingMaterialSummaryModal,
    AlertModal,
  },
  data() {
    return {
      // constants
      PROCESS_STATUS_NOT_START: 0,
      PROCESS_STATUS_WORKING: 1,
      PROCESS_STATUS_PAUSE: 2,
      apiToken: this.$store.state.api_token,
      isProcessing: false,
      // form data
      processEmployee: null,
      purchasedItem: null,
      brand: null,
      inputAmount: '',
      // resources
      processEmployeeList: [],
      materialList: [],
      brandList: [],
      // form status
      processStatus: 0,
      totalWorkingTimeInSeconds: 0,
      totalPauseTimeInSeconds: 0,
      totalPauseTimeComplete: 0,
      processProductItemList: [],
      // timer
      workingInterval: null,
      pauseInterval: null,
      workingTimestamp: null,
      pauseTimestamp: null,
      // dialog process done
      showSummaryDialog: false,
      showWarningPauseProcessDialog: false,
      showSaveProcessSuccessDialog: false,
      // dialog error summary
      showErrorSummary: false,
      showErrorMessage: '',
    };
  },
  methods: {
    customEmployeeFilter(options, search) {
      if (!search) return options;

      const fullWidthSearch = normalizeToKatakana(search.toLowerCase());

      return options.filter((option) => {
        const fullWidthOption = normalizeToKatakana(option['社員名称'].toLowerCase());
        return fullWidthOption.includes(fullWidthSearch);
      });
    },
    customPurchasedItemFilter(options, search) {
      if (!search) return options;

      const fullWidthSearch = normalizeToKatakana(search.toLowerCase());

      return options.filter((option) => {
        const fullWidthOption = normalizeToKatakana(option['仕入品名称'].toLowerCase());
        return fullWidthOption.includes(fullWidthSearch);
      });
    },
    customBrandFilter(options, search) {
      if (!search) return options;

      const fullWidthSearch = normalizeToKatakana(search.toLowerCase());

      return options.filter((option) => {
        const fullWidthOption = normalizeToKatakana(option['ブランド名称'].toLowerCase());
        return fullWidthOption.includes(fullWidthSearch);
      });
    },

    confirmLeave(to, from, next) {
      const isProcessNotStarted = this.processStatus === this.PROCESS_STATUS_NOT_START;

      if (isProcessNotStarted) {
        next();
        return;
      }

      if (to.name === 'Login') {
        this.handlePauseProcess();
        next();
        return;
      }

      if (this.processStatus === this.PROCESS_STATUS_WORKING) {
        this.showWarningPauseProcessDialog = true;
        next(false);
        return;
      }

      if (this.processStatus === this.PROCESS_STATUS_PAUSE) {
        this.handlePauseProcess();
      }

      next();
    },
    handleBeforeUnload() {
      // event.preventDefault();
      if (this.processStatus !== this.PROCESS_STATUS_NOT_START) {
        this.handlePauseProcess();
      }
      // event.returnValue = '';
    },
    // utils
    getCurrentDate: function () {
      return moment(new Date()).format("YYYYMMDD");
      // return '20240911';
    },
    formatTime(seconds) {
      const hours = Math.floor(seconds / 3600)
        .toString()
        .padStart(2, "0");
      const minutes = Math.floor((seconds % 3600) / 60)
        .toString()
        .padStart(2, "0");
      const secs = (seconds % 60).toString().padStart(2, "0");
      return `${hours}:${minutes}:${secs}`;
    },
    timeStringToSeconds(timeString) {
      const [hours, minutes, seconds] = timeString.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    },
    resetForm() {
      if (this.pauseInterval) clearInterval(this.pauseInterval);
      if (this.workingInterval) clearInterval(this.workingInterval);

      this.isProcessing = false;
      // form data
      this.processEmployee = null;
      this.purchasedItem = null;
      this.brand = null;
      this.inputAmount = '';
      // resources
      this.brandList = [];
      // form status
      this.processStatus = this.PROCESS_STATUS_NOT_START;
      this.totalWorkingTimeInSeconds = 0;
      this.totalPauseTimeInSeconds = 0;
      this.processProductItemList = [];
      // timer
      this.workingInterval = null;
      this.pauseInterval = null;
      this.workingTimestamp = null;
      this.pauseTimestamp = null;

      // reset form
      this.$refs.observer.reset();
    },

    // form events
    handleWorkingProcess() {
      this.isProcessing = true;
      // send API A017 create_temp_data
      if (this.processStatus === this.PROCESS_STATUS_PAUSE) {
        this.updateTempData();
      }

      this.processStatus = this.PROCESS_STATUS_WORKING;

      if (this.pauseInterval) clearInterval(this.pauseInterval);
      this.workingTimestamp = Date.now() - this.totalWorkingTimeInSeconds * 1000;
      this.workingInterval = setInterval(() => {
        this.totalWorkingTimeInSeconds = Math.floor((Date.now() - this.workingTimestamp) / 1000);
      }, 1000);
    },
    async handlePauseProcess() {
      // send API A015 create_temp_data
      await this.createTempData();

      // change process status
      this.processStatus = this.PROCESS_STATUS_PAUSE;
      if (this.workingInterval) clearInterval(this.workingInterval);
      this.pauseTimestamp = Date.now() - this.totalPauseTimeInSeconds * 1000;
      this.pauseInterval = setInterval(() => {
        this.totalPauseTimeInSeconds = Math.floor((Date.now() - this.pauseTimestamp) / 1000);
      }, 1000);
    },
    async handleDoneProcess() {
      if (this.processEmployee === null) {
        this.$refs.observer.setErrors({
          processEmployee: 'この項目は必須です',
        });
        return;
      }

      // stop working timer
      if (this.processStatus == this.PROCESS_STATUS_WORKING) {
        if (this.workingInterval) clearInterval(this.workingInterval);
        await this.createTempData();
        this.processStatus = this.PROCESS_STATUS_PAUSE;
      }
      // stop pause timer
      if (this.pauseInterval) clearInterval(this.pauseInterval);

      // send API A016 get_process_done_data
      await this.loadProcessDoneData();
      this.totalPauseTimeComplete = this.totalPauseTimeInSeconds;
      this.showSummaryDialog = true;
    },
    async startProcessing() {
      this.handleWorkingProcess();
    },
    async handleCloseSummaryModal(isContinueProcess) {
      if (isContinueProcess) {
        // send API A015
        await this.createTempData();
      }
      this.showSummaryDialog = false;
    },
    handlePurchasedItemChange() {
      (async () => {
        await this.loadBrandList();
        await this.loadTempData();
      })();
    },
    handleEndProcess() {
      this.resetForm();
      this.showSummaryDialog = false;
      this.showSaveProcessSuccessDialog = true;
    },
    handleErrorSummary(message) {
      this.showErrorSummary = true;
      this.showErrorMessage = message;
    },

    // events load data from API
    async loadProcessEmployeeList() {
      //API A011
      const processedDate = this.getCurrentDate();
      await axios
        .get("/api/get_process_employee.php",
          {
            params: {
              api_token: this.apiToken,
              store_cd: this.$store.state.tenpoCd,
              processed_date: processedDate,
            }
          }
        )
        .then(response => {
          this.processEmployeeList = response.data;
        })
        .catch(error => (
          console.log(error)
        ))
    },
    async loadMaterialList() {
      //API A012
      const processedDate = this.getCurrentDate();
      await axios
        .get("/api/get_raw_materials.php",
          {
            params: {
              api_token: this.apiToken,
              store_cd: this.$store.state.tenpoCd,
              processed_date: processedDate,
            }
          }
        )
        .then(response => {
          this.materialList = response.data;
        })
        .catch(error => (
          console.log(error)
        ))
    },
    async loadBrandList() {
      //API A014
      this.brand = undefined;

      if (this.purchasedItem) {
        await axios
          .get("/api/get_brand_list.php",
            {
              params: {
                api_token: this.apiToken,
                purchased_item_cd: this.purchasedItem['仕入品コード'],
              }
            }
          )
          .then(response => {
            this.brandList = response.data;
          })
          .catch(error => (
            console.log(error)
          ));
      } else {
        this.brandList = [];
      }
    },
    async loadTempData() {
      //API A013
      if (this.purchasedItem) {
        const processedDate = this.getCurrentDate();
        await axios
          .get("/api/get_temp_data.php",
            {
              params: {
                api_token: this.apiToken,
                store_cd: this.$store.state.tenpoCd,
                purchased_item_cd: this.purchasedItem['仕入品コード'],
                processed_date: processedDate,
              }
            }
          )
          .then(response => {
            const tempData = response.data;
            if (tempData == null) {
              this.brand = undefined;
              this.inputAmount = '';
              this.totalWorkingTimeInSeconds = 0;
              this.totalPauseTimeInSeconds = 0;
              this.processStatus = this.PROCESS_STATUS_NOT_START;
            } else {
              const brandCode = tempData['ブランド'];
              this.brand = this.brandList.find((item) => item['ブランドコード'] === brandCode);
              this.inputAmount = tempData['投入数量'] ? (tempData['投入数量'] == 0 ? '' : tempData['投入数量']) : '';
              this.totalWorkingTimeInSeconds = tempData['加工時間'] ? this.timeStringToSeconds(tempData['加工時間']) : 0;
              this.totalPauseTimeInSeconds = tempData['中断時間'] ?? 0;
              this.processStatus = this.PROCESS_STATUS_PAUSE;
            }
          })
          .catch(error => (
            console.log(error)
          ));
      } else {
        this.brandList = [];
      }
    },
    async createTempData() {
      //API A015
      if (this.purchasedItem) {
        const processedDate = this.getCurrentDate();
        await axios
          .post("/api/create_temp_data.php", {
            api_token: this.apiToken,
            store_cd: this.$store.state.tenpoCd,
            purchased_item_cd: this.purchasedItem['仕入品コード'],
            bland_cd: this.brand?.['ブランドコード'] ?? '',
            input_amount: Number(this.inputAmount),
            processed_time: this.formatTime(this.totalWorkingTimeInSeconds),
            processed_date: processedDate,
          })
          .then(response => {
            this.totalPauseTimeInSeconds = response.data['中断時間'];
          })
          .catch(error => (
            console.log(error)
          ));
      }
    },
    async updateTempData() {
      //API A017
      if (this.purchasedItem) {
        const processedDate = this.getCurrentDate();
        await axios
          .post("/api/update_temp_data.php", {
            api_token: this.apiToken,
            store_cd: this.$store.state.tenpoCd,
            purchased_item_cd: this.purchasedItem['仕入品コード'],
            processed_date: processedDate,
          })
          .then(response => {
            this.totalPauseTimeInSeconds = response.data['中断時間'];
          })
          .catch(error => (
            console.log(error)
          ));
      }
    },
    async loadProcessDoneData() {
      //API A016
      if (this.purchasedItem) {
        await axios
          .get("/api/get_process_done_data.php",
            {
              params: {
                api_token: this.apiToken,
                purchased_item_cd: this.purchasedItem['仕入品コード'],
              }
            }
          )
          .then(response => {
            this.processProductItemList = response.data;
          })
          .catch(error => (
            console.log(error)
          ));
      }
    },
  },
  created() {
    axios.defaults.baseURL = process.env.VUE_APP_API_ENDPOINT
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    this.loadProcessEmployeeList();
    this.loadMaterialList();
  },
  beforeUnmount() {
    clearInterval(this.workingInterval);
    clearInterval(this.pauseInterval);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    this.confirmLeave(to, from, next);
  },
};
</script>

<style scoped>
.error-text {
  color: red;
}

.input-amount {
  text-align: left !important;
  width: 100%;
  border: 1px solid rgba(60, 60, 60, .26);
  border-radius: 4px;
  padding: 8px;
  font-size: 20px;
}

.input-amount:disabled {
  background-color: #f8f8f8;
}

/* For Chrome, Safari, Edge, and Opera */
.input-amount[type="number"]::-webkit-outer-spin-button,
.input-amount[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* For Firefox */
.input-amount[type="number"] {
  -moz-appearance: textfield;
}

.working-time-text {
  font-size: 36px;
  line-height: 1.5em;
  box-sizing: border-box;
  color: #000;
}

.pause-time-text {
  font-size: 18px;
  color: #000;
}

.pause-item-value {
  font-size: 24px;
}

/** responsive tablet */
@media only screen and (min-width: 960px) {
  .form-processing-material {
    height: calc(100vh - 33px);
  }

  .form-processing-material-validation {
    display: block;
    height: 100%;
  }

  .form-processing-material-validation>form {
    display: flex;
    flex-direction: column;
    height: 100%
  }

  .counter-bar {
    display: flex;
    flex-direction: column;
  }

  .working-time-text {
    font-size: 88px;
    letter-spacing: 4pt;
  }

  .pause-time-text {
    font-size: 36px;
  }

  .pause-item-value {
    font-size: 48px;
    letter-spacing: 4pt;
  }

  .v-btn.v-size--large {
    min-width: 200px;
    font-size: 32px;
    height: 72px;
  }
}
</style>
